import React, { useEffect, useState } from "react";
import { Box, Card, Typography, Grid } from "@mui/material";

const COLORS = [
  { bg: "#f2ebff", text: "#4a148c", iconBg: "rgba(106,27,154,0.12)", badge: "#6a1b9a" },
  { bg: "#e0f7fa", text: "#006064", iconBg: "rgba(0,151,167,0.12)", badge: "#0097a7" },
  { bg: "#fff3e0", text: "#e65100", iconBg: "rgba(230,81,0,0.12)",   badge: "#e65100" },
  { bg: "#fce4ec", text: "#880e4f", iconBg: "rgba(173,20,87,0.12)",  badge: "#ad1457" },
  { bg: "#e8f5e9", text: "#1b5e20", iconBg: "rgba(46,125,50,0.12)",  badge: "#2e7d32" },
];

function ChangeChip({ value, label }) {
  const up = value >= 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.3,
          px: 0.8,
          py: 0.2,
          borderRadius: 10,
          fontSize: "0.7rem",
          fontWeight: 700,
          bgcolor: up ? "rgba(46,125,50,0.1)" : "rgba(198,40,40,0.1)",
          color: up ? "#2e7d32" : "#c62828",
        }}
      >
        {up ? "↑" : "↓"} {Math.abs(value)}%
      </Box>
      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem" }}>
        vs {label}
      </Typography>
    </Box>
  );
}

function KpiCard({
  label,
  value, // main value (amount for purchase, count for users)
  records, // additional records label
  sub, // main sub value
  subRecords, // sub records value
  subLabel,
  change, // main change %
  changeLabel,
  colorIdx,
  loading,
  isPurchase
}) {
  const c = COLORS[colorIdx % COLORS.length];
  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 125,
          borderRadius: 2.5,
          bgcolor: c.bg,
          overflow: "hidden",
          position: "relative",
          border: "1px solid rgba(145,158,171,0.08)",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: "-100%",
            width: "60%",
            height: "100%",
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.65), transparent)",
            animation: "kpiShimmer 1.6s infinite",
          },
          "@keyframes kpiShimmer": {
            "0%":   { left: "-60%" },
            "100%": { left: "120%" },
          },
        }}
      />
    );
  }

  const formatVal = (val) => {
    if (val === undefined || val === null) return "—";
    return isPurchase ? `₹${Number(val).toLocaleString("en-IN")}` : val.toLocaleString("en-IN");
  };

  const formatSub = (val) => {
    if (val === undefined || val === null) return "—";
    return isPurchase ? `₹${Number(val).toLocaleString("en-IN")}` : val.toLocaleString("en-IN");
  };

  return (
    <Card
      sx={{
        bgcolor: c.bg,
        border: "1px solid rgba(145,158,171,0.08)",
        boxShadow: "0 2px 12px 0 rgba(145,158,171,0.08)",
        borderRadius: 2.5,
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between"
      }}
    >
      <Box>
        <Typography
          variant="caption"
          sx={{ color: c.text, fontWeight: 700, opacity: 0.65, textTransform: "uppercase", letterSpacing: 0.6 }}
        >
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, color: c.text, mt: 0.5, mb: 0.2 }}>
          {formatVal(value)}
        </Typography>
        
        {/* Additional records display for purchase type */}
        {isPurchase && records !== undefined && (
          <Typography variant="body2" sx={{ color: c.text, fontWeight: 600, opacity: 0.8, fontSize: "0.82rem" }}>
            {records.toLocaleString("en-IN")} records
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: 1 }}>
        {change !== undefined && (
          <ChangeChip value={change} label={changeLabel} />
        )}
        {sub !== undefined && (
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5, fontSize: "0.72rem" }}>
            {subLabel}: {formatSub(sub)} {isPurchase && subRecords !== undefined && `(${subRecords} recs)`}
          </Typography>
        )}
      </Box>
    </Card>
  );
}

export default function KpiStrip({ type = "purchase" }) {
  const [data, setData] = useState(() => {
    try {
      const cached = localStorage.getItem(`kpi_cache_${type}`);
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });
  const [loading, setLoading] = useState(!data);
  const token = localStorage.getItem("token");

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_LIVE_URL_NEW}/admin/get-kpis?type=${type}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        if (json?.data) {
          setData(json.data);
          localStorage.setItem(`kpi_cache_${type}`, JSON.stringify(json.data));
        }
      } catch (e) {
        console.error("KPI fetch error:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [type, token]);

  const isPurchase = type === "purchase";

  const cards = isPurchase
    ? [
        {
          label: "Total Revenue",
          value: data?.total?.amount,
          records: data?.total?.count,
          colorIdx: 0,
        },
        {
          label: "Today",
          value: data?.today?.amount,
          records: data?.today?.count,
          colorIdx: 1,
        },
        {
          label: "This Week",
          value: data?.thisWeek?.amount,
          records: data?.thisWeek?.count,
          sub: data?.lastWeek?.amount,
          subRecords: data?.lastWeek?.count,
          subLabel: "Last week",
          change: data?.weekChangeAmount,
          changeLabel: "last week",
          colorIdx: 2,
        },
        {
          label: "This Month",
          value: data?.thisMonth?.amount,
          records: data?.thisMonth?.count,
          sub: data?.lastMonth?.amount,
          subRecords: data?.lastMonth?.count,
          subLabel: "Last month",
          change: data?.monthChangeAmount,
          changeLabel: "last month",
          colorIdx: 3,
        },
        {
          label: "Last Month",
          value: data?.lastMonth?.amount,
          records: data?.lastMonth?.count,
          colorIdx: 4,
        },
      ]
    : [
        {
          label: "Total Users",
          value: data?.total?.count,
          colorIdx: 0,
        },
        {
          label: "Today",
          value: data?.today?.count,
          colorIdx: 1,
        },
        {
          label: "This Week",
          value: data?.thisWeek?.count,
          sub: data?.lastWeek?.count,
          subLabel: "Last week",
          change: data?.weekChangeCount,
          changeLabel: "last week",
          colorIdx: 2,
        },
        {
          label: "This Month",
          value: data?.thisMonth?.count,
          sub: data?.lastMonth?.count,
          subLabel: "Last month",
          change: data?.monthChangeCount,
          changeLabel: "last month",
          colorIdx: 3,
        },
        {
          label: "Last Month",
          value: data?.lastMonth?.count,
          colorIdx: 4,
        },
      ];

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2}>
        {cards.map((c, i) => (
          <Grid item xs={6} sm={4} md={2.4} key={i}>
            <KpiCard {...c} loading={loading} isPurchase={isPurchase} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
